import { Button, Card, CardActions, CardContent, Grid, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import * as Yup from "yup";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
    ID_ACTIVE,
    ID_ANCILLARIES_EXTRA,
    ID_CORPORATE_EXTRA,
    ID_INACTIVE,
    ID_RETAIL_EXTRA,
    ID_STANDARD
} from "../constants/ClusterStatuses";
import { ID_LICENSEES, ID_MANDATORY } from "../constants/ClusterMSTypes";
import { useClustersRepository } from "../../../api/repositories/ClustersRepository";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import FormTextField from "../../../experiments/forms/FormTextField";
import FormDatePicker from "../../../experiments/forms/FormDatePicker";
import FormSelect from "../../../experiments/forms/FormSelect";
import { useState } from 'react';

type ClusterInputs = {
    name: string,
    type: string,
    clusterType: string,
    validDateFrom: Date | undefined,
    validDateTo: Date | undefined,
    status: string,
    legalEntity: string,
    dealer: string
};

const CreateCluster = () => {

    const { t } = useTranslation("finance", { keyPrefix: 'clusters' });
    const { t: tg } = useTranslation("translation");

    const navigate = useNavigate();
    const { createCluster } = useClustersRepository();
    const { dispatchGlobalData, globalData: { user }  } = React.useContext(GlobalContext);

    const legalEntityOptions: any[] = user.legalEntities
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
        value: x.name,
        label: tg(`legalEntities.${x.name}`),
        }));


    const dealerOptions: any[] = user.dealers
        ?.filter((x: any) => x.name.toLowerCase() !== "all")
        .map((x: any) => ({
            value: x.name,
            label: tg(`dealers.${x.name}`),
        }));

    const [dealers, setDealers] = useState<any[]>(dealerOptions);

    const defaultValues: ClusterInputs = {
        name: '',
        type: '',
        clusterType: ID_STANDARD,
        validDateFrom: undefined,
        validDateTo: undefined,
        status: '',
        legalEntity: legalEntityOptions.length == 1 ? legalEntityOptions[0].value : "",
        dealer: dealerOptions.length == 1 ? dealerOptions[0].value : "",
    }

    const schema = Yup.object().shape({
        name: Yup.string().required(),
        type: Yup.string().required(),
        clusterType: Yup.string().required(),
        validDateFrom: Yup.date().required().test("testFrom", t('cluster-wizard.form.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.validDateTo || (value <= context.parent.validDateTo);
        }).typeError(t('cluster-wizard.form.errors.invalid-date')),
        validDateTo: Yup.date().required().test("testTo", t('cluster-wizard.form.errors.invalid-date'), (value, context) => {
            return !value || !context.parent.validDateFrom || (value >= context.parent.validDateFrom);
        }).typeError(t('cluster-wizard.form.errors.invalid-date')),
        status: Yup.string().required(),
        legalEntity: Yup.string().required(),
        dealer: Yup.string().required()        
    });

    const { handleSubmit, control, watch } = useForm<ClusterInputs>({
        defaultValues: defaultValues,
        resolver: yupResolver(schema)
    });
    const watcher = watch();


    const onSubmit: SubmitHandler<ClusterInputs> = data => {
        createCluster({
            name: data.name,
            msTypology: data.type,
            type: data.clusterType,
            validFrom: data.validDateFrom || new Date(),
            validTo: data.validDateTo || new Date(),
            validStatus: data.status,
            legalEntity: data.legalEntity,
            dealer: data.dealer,            
        }).then((data) => {
            if(data.status === 'OK') {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('cluster-wizard.snackbar.success'),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true
                        }
                    }
                });
                navigate('/finance/clusters');
            } else {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('error-dialog.errors.cluster.name-already-in-use'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true
                        }
                    }
                });
            }
        });
    }


    const types = [
        {
            value: ID_MANDATORY,
            label: t('ms-types.' + ID_MANDATORY)
        },
        {
            value: ID_LICENSEES,
            label: t('ms-types.' + ID_LICENSEES)
        }
    ];

    const statuses = [
        {
            value: ID_ACTIVE,
            label: t('statuses.' + ID_ACTIVE),
        },
        {
            value: ID_INACTIVE,
            label: t('statuses.' + ID_INACTIVE),
        }
    ];

    const clusterTypes = [
        {
            value: ID_STANDARD,
            label: t('cluster-types.' + ID_STANDARD)
        },
        {
            value: ID_RETAIL_EXTRA,
            label: t('cluster-types.' + ID_RETAIL_EXTRA)
        },
        {
            value: ID_CORPORATE_EXTRA,
            label: t('cluster-types.' + ID_CORPORATE_EXTRA)
        },
        {
            value: ID_ANCILLARIES_EXTRA,
            label: t('cluster-types.' + ID_ANCILLARIES_EXTRA)
        }
    ];

const handleChangeLegalEntities = (value: any) => {
        if (value == '') {
            setDealers([]);
            return;
        }
        let dealers = user?.dealers
            ?.filter(
                (x: any) =>
                    x.legalEntities.some((e: any) => value == e.name) &&
                    x.name.toLowerCase() !== "all"
            )
            .map((x: any) => {
            return {
                label: tg(`dealers.${x.name}`),
                value: x.name,
            };
        });
        setDealers(dealers);
    }

    return (
            <Card variant="outlined" sx={{ margin: '15px' }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <CardContent>
                        <Grid container spacing={2} alignItems={'flex-end'}>
                            <Grid item xs={12}>
                                <Typography variant={'body2'} color={'primary.light'} fontWeight={700}>
                                    {t('cluster-wizard.form.title')}
                                </Typography>
                            </Grid>
                            <Grid item  md={4} sm={4} xs={12}>
                                <FormTextField
                                        name={"name"}
                                        control={control}
                                        props={{
                                            label: t('cluster-wizard.form.labels.name'),
                                            fullWidth: true,
                                        }}
                                />
                            </Grid>


                            <Grid item md={4} sm={4} xs={12}>
                                <FormSelect
                                    name={"legalEntity"}
                                    control={control}
                                    props={{
                                        label: tg("shared.legalEntity"),
                                        fullWidth: true,
                                        required: true,
                                        onChange: (e: any) => {
                                            handleChangeLegalEntities(e.target.value)
                                        },
                                    }}
                                    
                                    items={legalEntityOptions.map((option: any) => (
                                        <MenuItem key={option.value} 
                                                    value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                />                       
                            </Grid>
                            <Grid item md={4} sm={4} xs={12}>
                            <FormSelect
                                    name={"dealer"}
                                    control={control}
                                    props={{
                                        label: tg("shared.dealer"),
                                        fullWidth: true,
                                        required: true,
                                        disabled: dealers.length == 0
                                    }}
                                    items={dealers.map((option: any) => (
                                        <MenuItem key={option.value}
                                                    value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                />
                            </Grid>


                            <Grid item md={4} sm={4} xs={12}>
                                <FormSelect
                                        name={"type"}
                                        control={control}
                                        props={{
                                            label: t('cluster-wizard.form.labels.type'),
                                            fullWidth: true,
                                        }}
                                        items={types.map((option: any) => (
                                                <MenuItem key={option.value}
                                                          value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                        ))}
                                />
                            </Grid>
                            <Grid item md={4} sm={4} xs={12}>
                                <FormSelect
                                        name={"clusterType"}
                                        control={control}
                                        props={{
                                            label: t('cluster-wizard.form.labels.clusterType'),
                                            fullWidth: true,
                                        }}
                                        items={clusterTypes.map((option: any) => (
                                                <MenuItem key={option.value}
                                                          value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                        ))}
                                />
                            </Grid>
                            <Grid item md={4} sm={4} xs={12}>
                                <FormSelect
                                    name={"status"}
                                    control={control}
                                    props={{
                                        label: t('cluster-wizard.form.labels.status'),
                                        fullWidth: true,
                                    }}
                                    items={statuses.map((option: any) => (
                                        <MenuItem key={option.value}
                                                  value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormDatePicker
                                        name={"validDateFrom"}
                                        control={control}
                                        props={{
                                            label: t('cluster-wizard.form.labels.valid-from'),
                                            maxDate: watcher.validDateTo || undefined
                                        }}
                                />
                            </Grid>
                            <Grid item md={3} sm={6} xs={12}>
                                <FormDatePicker
                                        name={"validDateTo"}
                                        control={control}
                                        props={{
                                            label: t('cluster-wizard.form.labels.valid-to'),
                                            minDate: watcher.validDateFrom || undefined
                                        }}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end' }}>
                        <Button variant={"outlined"} color={"accent"} onClick={() => navigate('/finance/clusters/')}>{t('cluster-wizard.buttons.cancel')}</Button>
                        <Button color={"accent"} type="submit">{t('cluster-wizard.buttons.create')}</Button>
                    </CardActions>
                </form>
            </Card>
    )
}

export default CreateCluster;